/* eslint-disable @next/next/no-img-element */
import { Fragment, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useRecoilState } from "recoil";
import { useLazyQuery } from "@apollo/client";
import { getLocalStorage, setLocalStorage } from "../../helpers/storage";

import { GET_PLATFORM_OPTIONS } from "../../lib/graphql/general.queries";
import { engageState } from "../../atoms/platform";
import { engageEmbedState } from "../../atoms/engage";

import { Dialog, Transition } from "@headlessui/react";
import {
  FolderIcon,
  HomeIcon,
  XIcon,
  SearchIcon,
} from "@heroicons/react/outline";
import { AiOutlineCalendar } from "react-icons/ai";

import Loading from "../Shared/Loading";
import Invalid from "../Engage/Invalid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function EngageSideBar({ sidebarOpen, setSidebarOpen, path }) {
  const router = useRouter();
  const { embed, client, cablecast_embed } = router.query;
  const [isEmbed, setIsEmbed] = useRecoilState(engageEmbedState);
  const [platform, setPlatform] = useRecoilState(engageState);
  const [getOptions, { data: options }] = useLazyQuery(GET_PLATFORM_OPTIONS, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
  });

  useEffect(() => {
    if (embed) {
      if (embed === "true") {
        setIsEmbed(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!platform) {
      const localStorage = getLocalStorage("platform");
      if (!localStorage) {
        getOptions({ variables: { clientId: client } });
      } else {
        if (platform?.onboarded) {
          setPlatform(localStorage);
        } else {
          getOptions({ variables: { clientId: client } });
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, platform]);

  useEffect(() => {
    if (options) {
      setPlatform(options.options);
      setLocalStorage("platform", options.options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  if (!platform) return <Loading />;

  if (platform && platform.id === "none") return <Invalid />;

  return (
    <>
      {!platform.onboarded && <Invalid />}
      {/* Standard Mobile SideBar */}
      {cablecast_embed === undefined && (
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            static
            className="fixed inset-0 flex z-40"
            open={sidebarOpen}
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div
                className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4"
                style={{ backgroundColor: platform.publicColor }}
              >
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XIcon
                        className="h-5 w-5 2xl:h-6 2xl:w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex-shrink-0 flex items-center px-4">
                  <img className="h-8 w-auto" src={platform.logo} alt="city logo" />
                </div>
                <div className="mt-5 flex-1 h-0 overflow-y-auto">
                  <nav className="px-2 space-y-1">
                    <Link
                      href="/engage/[client]"
                      as={`/engage/${client}`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <a
                        className={classNames(
                          path === "index"
                            ? "bg-indigo-800 text-white"
                            : "bg-transparent text-indigo-100 hover:bg-indigo-600",
                          "group flex items-center px-2 py-3 text-base font-medium rounded-md cursor-pointer"
                        )}
                      >
                        <HomeIcon className="mr-4 h-5 w-5 2xl:h-6 2xl:w-6 text-white" />
                        <p>Dashboard</p>
                      </a>
                    </Link>
                    {/* <Link
                    href="/engage/[client]/boards"
                    as={`/engage/${client}/boards`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <a
                      className={classNames(
                        path === "boards"
                          ? "bg-indigo-800 text-white"
                          : "bg-transparent text-indigo-100 hover:bg-indigo-600",
                        "group flex items-center px-2 py-3 text-base font-medium rounded-md cursor-pointer"
                      )}
                    >
                      <UsersIcon className="mr-4 h-5 w-5 2xl:h-6 2xl:w-6 text-white" />
                      <p>Boards</p>
                    </a>
                  </Link> */}
                    <Link
                      href="/engage/[client]/agendas"
                      as={`/engage/${client}/agendas`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <a
                        className={classNames(
                          path === "agendas"
                            ? "bg-indigo-800 text-white"
                            : "bg-transparent text-indigo-100 hover:bg-indigo-600",
                          "group flex items-center px-2 py-3 text-base font-medium rounded-md cursor-pointer"
                        )}
                      >
                        <FolderIcon className="mr-4 h-5 w-5 2xl:h-6 2xl:w-6 text-white" />
                        <p>Agendas</p>
                      </a>
                    </Link>
                    {/* <Link
                      href="/engage/[client]/speaker"
                      as={`/engage/${client}/speaker`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <a
                        className={classNames(
                          path === "speaker"
                            ? "bg-indigo-800 text-white"
                            : "bg-transparent text-indigo-100 hover:bg-indigo-600",
                          "group flex items-center px-2 py-3 text-base font-medium rounded-md cursor-pointer"
                        )}
                      >
                        <SpeakerphoneIcon className="mr-4 h-5 w-5 2xl:h-6 2xl:w-6 text-white" />
                        <p>Speak / Comment </p>
                      </a>
                    </Link> */}
                    <Link
                      href="/engage/[client]/search"
                      as={`/engage/${client}/search`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <a
                        className={classNames(
                          path === "search"
                            ? "bg-indigo-800 text-white"
                            : "bg-transparent text-indigo-100 hover:bg-indigo-600",
                          "group flex items-center px-2 py-3 text-base font-medium rounded-md cursor-pointer"
                        )}
                      >
                        <SearchIcon className="mr-4 h-5 w-5 2xl:h-6 2xl:w-6 text-white" />
                        <p>Search </p>
                      </a>
                    </Link>
                  </nav>
                </div>
              </div>
            </Transition.Child>
            <div className="absolute bottom-2">
              <p className="text-gray-300 italic text-sm ml-4">Powered by:</p>
              <p className="text-gray-300 text-sm ml-4">JustMeetings</p>
            </div>
            <div className="flex-shrink-0 w-14" aria-hidden="true">
              {/* Dummy element to force sidebar to shrink to fit close icon */}
            </div>
          </Dialog>
        </Transition.Root>
      )}

      {/* Static sidebar for desktop */}
      {!isEmbed && cablecast_embed === undefined && (
        <div
          className="hidden lg:flex lg:flex-shrink-0"
          style={{ backgroundColor: platform.publicColor }}
        >
          <div className="flex flex-col w-44 2xl:w-64">
            {/* Sidebar component, swap this element with another sidebar if you like */}
            <div className="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4 justify-center">
                <img
                  className="h-16 2xl:h-24 w-auto"
                  src={platform.logo}
                  alt="city logo"
                />
              </div>
              <div className="mt-5 flex-1 flex flex-col">
                <nav className="flex-1 px-2 space-y-1">
                  <Link href="/engage/[client]" as={`/engage/${client}`}>
                    <a
                      className={classNames(
                        path === "index"
                          ? "bg-indigo-800 text-white"
                          : "bg-transparent text-indigo-100 hover:bg-indigo-600",
                        "group flex items-center px-2 py-3 text-sm 2xl:text-base font-medium rounded-md cursor-pointer"
                      )}
                    >
                      <AiOutlineCalendar className="mr-3 h-5 w-5 2xl:h-6 2xl:w-6 text-white" />
                      <p>Calendar</p>
                    </a>
                  </Link>
                  {/* <Link
                    href="/engage/[client]/boards"
                    as={`/engage/${client}/boards`}
                  >
                    <a
                      className={classNames(
                        path === "boards"
                          ? "bg-indigo-800 text-white"
                          : "bg-transparent text-indigo-100 hover:bg-indigo-600",
                        "group flex items-center px-2 py-3 text-sm 2xl:text-base font-medium rounded-md cursor-pointer"
                      )}
                    >
                      <UsersIcon className="mr-3 h-5 w-5 2xl:h-6 2xl:w-6 text-white" />
                      <p>Boards</p>
                    </a>
                  </Link> */}
                  <Link
                    href="/engage/[client]/agendas"
                    as={`/engage/${client}/agendas`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <a
                      className={classNames(
                        path === "agendas"
                          ? "bg-indigo-800 text-white"
                          : "bg-transparent text-indigo-100 hover:bg-indigo-600",
                        "group flex items-center px-2 py-3 text-sm 2xl:text-base font-medium rounded-md cursor-pointer"
                      )}
                    >
                      <FolderIcon className="mr-3 h-5 w-5 2xl:h-6 2xl:w-6 text-white" />
                      <p>Agendas</p>
                    </a>
                  </Link>
                  {/* <Link
                    href="/engage/[client]/speaker"
                    as={`/engage/${client}/speaker`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <a
                      className={classNames(
                        path === "speaker"
                          ? "bg-indigo-800 text-white"
                          : "bg-transparent text-indigo-100 hover:bg-indigo-600",
                        "group flex items-center px-2 py-3 text-sm 2xl:text-base font-medium rounded-md cursor-pointer"
                      )}
                    >
                      <SpeakerphoneIcon className="mr-3 h-5 w-5 2xl:h-6 2xl:w-6 text-white" />
                      <p>Speak / Comment </p>
                    </a>
                  </Link> */}
                  <Link
                    href="/engage/[client]/search"
                    as={`/engage/${client}/search`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <a
                      className={classNames(
                        path === "search"
                          ? "bg-indigo-800 text-white"
                          : "bg-transparent text-indigo-100 hover:bg-indigo-600",
                        "group flex items-center px-2 py-3 text-sm 2xl:text-base font-medium rounded-md cursor-pointer"
                      )}
                    >
                      <SearchIcon className="mr-3 h-5 w-5 2xl:h-6 2xl:w-6 text-white" />
                      <p>Search</p>
                    </a>
                  </Link>
                  <div className="absolute bottom-2">
                    <p className="text-gray-300 italic text-xs 2xl:text-sm ml-2">
                      Powered by:
                    </p>
                    <p className="text-gray-300 text-xs 2xl:text-sm ml-2">
                      JustMeetings
                    </p>
                  </div>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EngageSideBar;
